import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
}

/**
 * Polite status region used when a list / query returns zero results.
 *
 * SR users hear the title + description on insertion. The icon is decorative —
 * `aria-hidden` keeps it out of the SR announcement.
 *
 * WCAG 4.1.3 Status Messages (Level AA).
 */
export default function EmptyState({ title, description, icon, action }: EmptyStateProps) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        py: 6,
        textAlign: 'center'
      }}
    >
      <Box aria-hidden sx={{ color: 'text.disabled', mb: 1 }}>
        {icon ?? <InboxOutlinedIcon sx={{ fontSize: 64, opacity: 0.4 }} />}
      </Box>
      <Typography variant="h6" color="text.primary">
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
      {action}
    </Box>
  );
}
